import { useEffect, useState, useDeferredValue, useTransition } from 'react';
import { Persistor } from 'redux-persist';

/**
 * Modern React 18 hook for deferred Redux persist rehydration
 * Uses concurrent features for optimal performance
 */
export const useDeferredRehydration = (persistor: Persistor) => {
  const [isRehydrated, setIsRehydrated] = useState(false);
  const [isPending, startTransition] = useTransition();
  
  // Defer the rehydration state to prevent blocking renders
  const deferredIsRehydrated = useDeferredValue(isRehydrated);

  useEffect(() => {
    // Check if already rehydrated
    const { bootstrapped } = persistor.getState();
    if (bootstrapped) {
      setIsRehydrated(true);
      return;
    }

    // Start rehydration in a transition (non-blocking)
    startTransition(() => {
      const unsubscribe = persistor.subscribe(() => {
        const { bootstrapped } = persistor.getState();
        if (bootstrapped) {
          setIsRehydrated(true);
        }
      });

      // Trigger rehydration
      persistor.persist();

      return () => unsubscribe();
    });
  }, [persistor]);

  return {
    isRehydrated: deferredIsRehydrated,
    isPending,
  };
};